import pick from 'lodash/pick';
import { path, pathOr } from 'ramda';

import config from '../../config';

import { getCouponDiscount, initiatePrivileged, transitionPrivileged } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import {
  TRANSITION_REQUEST_PAYMENT,
  TRANSITION_REQUEST_PAYMENT_AFTER_ENQUIRY,
  TRANSITION_CONFIRM_PAYMENT,
  isPrivileged,
} from '../../util/transactions';
import * as log from '../../util/log';
import {
  fetchCurrentUserHasOrdersSuccess,
  fetchCurrentUser,
  getUserToken,
} from '../../ducks/user.duck';
import { requestAction } from '../../util/eventsHelpers/constants';
import { sendEventByAction } from '../../util/eventsHelpers';
import { maskContactDetails } from '../../util/contactDetails';
import {updateProfile} from "../ProfileSettingsPage/ProfileSettingsPage.duck";
import { convertMomentDatesToISO } from '../../util/dates';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/ListingPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/ListingPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/ListingPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const RESET_DISCOUNT_CODE = 'app/CheckoutPage/RESET_DISCOUNT_CODE';
export const VERIFY_DISCOUNT_CODE_REQUESTED = 'app/CheckoutPage/VERIFY_DISCOUNT_CODE_REQUESTED';
export const VERIFY_DISCOUNT_CODE_SUCCESS = 'app/CheckoutPage/VERIFY_DISCOUNT_CODE_SUCCESS';
export const VERIFY_DISCOUNT_CODE_ERROR = 'app/CheckoutPage/VERIFY_DISCOUNT_CODEERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  bookingData: null,
  bookingDates: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  isLoadingSpeculativeBooking: false,
  isLoadingVerifyDiscountCode: false,
  errorVerifyDiscountCode: null,
  resultVerifyDiscountCode: null,
  discount: {},
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS:
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
      };
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    case RESET_DISCOUNT_CODE:
      return {
        ...state,
        isLoadingVerifyDiscountCode: false,
        errorVerifyDiscountCode: null,
        resultVerifyDiscountCode: null,
        discount: null,
      };
    case VERIFY_DISCOUNT_CODE_REQUESTED:
      return {
        ...state,
        isLoadingVerifyDiscountCode: true,
        errorVerifyDiscountCode: null,
        resultVerifyDiscountCode: null,
      };
    case VERIFY_DISCOUNT_CODE_SUCCESS:
      return {
        ...state,
        isLoadingVerifyDiscountCode: false,
        errorVerifyDiscountCode: null,
        resultVerifyDiscountCode: true,
        discount: payload.discount,
      };
    case VERIFY_DISCOUNT_CODE_ERROR:
      return {
        ...state,
        isLoadingVerifyDiscountCode: false,
        errorVerifyDiscountCode: payload,
        resultVerifyDiscountCode: false,
        discount: {},
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = (initialValues) => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = (order) => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = (e) => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = (orderId) => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = (e) => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = (transaction) => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = (e) => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = (e) => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const verifyDiscountCodeRequest = () => ({
  type: VERIFY_DISCOUNT_CODE_REQUESTED,
  payload: {
  },
});

export const verifyDiscountCodeSuccess = (discount) => ({
  type: VERIFY_DISCOUNT_CODE_SUCCESS,
  payload: {
    discount,
  },
});
export const verifyDiscountCodeError = (e) => ({
  type: VERIFY_DISCOUNT_CODE_ERROR,
  error: true,
  payload: e,
});

export const resetDiscountCode = () => ({
  type: RESET_DISCOUNT_CODE,
});

/* ================ Thunks ================ */

export const initiateOrder = (orderParams, transactionId) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  const state = getState();
  const bodyParams = orderParams;
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };
  const discount = pathOr(null, ['CheckoutPage', 'discount', 'discount'], state);

  const token = getUserToken();
  const transactionIdFromEnquire =
    transactionId && transactionId.uuid !== 'estimated-transaction' && transactionId.uuid;

  const handleSuccess = (response) => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = (e) => {
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      bookingStart: orderParams.bookingStart,
      bookingEnd: orderParams.bookingEnd,
    });
    throw e;
  };

  return initiatePrivileged(
    {
      isSpeculative: false,
      bodyParams,
      queryParams,
      bookingIdFromEnquire: transactionIdFromEnquire,
      discount,
    },
    token
  )
    .then(handleSuccess)
    .catch(handleError);
};

export const confirmPayment = (orderParams) => (dispatch, getState, sdk) => {
  dispatch(confirmPaymentRequest());

  const bodyParams = {
    id: orderParams.transactionId,
    transition: TRANSITION_CONFIRM_PAYMENT,
    params: {
      protectedData: {
        ...(orderParams.companyBillingDetailsMaybe.iAmACompany ?
          orderParams.companyBillingDetailsMaybe : {}),
      },
    },
  };

  return sdk.transactions
    .transition(bodyParams)
    .then((response) => {
      const order = response.data.data;
      dispatch(confirmPaymentSuccess(order.id));
      dispatch(updateProfile({
        publicData: {
          paymentMethodVerified: true,
        },
      }));
      return order;
    })
    .catch((e) => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = orderParams.transactionId
        ? { transactionId: orderParams.transactionId.uuid }
        : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const sendMessage = (params) => (dispatch, getState, sdk) => {
  const message = params.message && maskContactDetails(params.message.trim());
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        sendEventByAction(orderId.uuid, requestAction);
        return { orderId, messageSuccess: true };
      })
      .catch((e) => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (orderParams, transactionId) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  const params = {
    ...orderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = orderParams;

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };
  const state = getState();
  const discount = pathOr(null, ['CheckoutPage', 'discount', 'discount'], state);

  const token = getUserToken();
  const transactionIdFromEnquire =
    transactionId && transactionId.uuid !== 'estimated-transaction' && transactionId.uuid;

  const handleSuccess = (response) => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = (e) => {
    const { listingId, bookingStart, bookingEnd } = params;
    log.error(e, 'speculate-transaction-failed', {
      listingId,
      bookingStart,
      bookingEnd,
      ...params,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

console.log('initiatePrivileged', {
  isSpeculative: true,
  bodyParams,
  queryParams,
  bookingIdFromEnquire: transactionIdFromEnquire,
  discount,
})
  return initiatePrivileged(
    {
      isSpeculative: true,
      bodyParams,
      queryParams,
      bookingIdFromEnquire: transactionIdFromEnquire,
      discount,
    },
    token
  )
    .then(handleSuccess)
    .catch(handleError);
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then((response) => {
      dispatch(stripeCustomerSuccess());
    })
    .catch((e) => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};

export const verifyDiscountCode = (params) => async (dispatch, getState, sdk) => {
  try {
    dispatch(verifyDiscountCodeRequest());
    const { discountCode, orderParams, setStatus, transactionId } = params;
    const token = getUserToken();
    const response = await getCouponDiscount({ coupon: discountCode }, token);
    const discount = {
      percentage: path(['discount', 'percent_off'], response),
      code: path(['code'], response),
    };

    const { status } = response;
    if (status < 200 || status >= 300) {
      dispatch(verifyDiscountCodeError({}));
      return;
    }

    dispatch(verifyDiscountCodeSuccess({ discount }));
    if (orderParams && transactionId)
      dispatch(speculateTransaction(convertMomentDatesToISO(orderParams), transactionId));
  } catch (error) {
    dispatch(verifyDiscountCodeError(error));
    console.log('error redux', error);
  }
};
